import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Play, Pause, Music, Mic, Volume2, Download } from 'lucide-react';

const AudioLibraryPage = () => {
  const navigate = useNavigate();
  const [currentPlaying, setCurrentPlaying] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  const guidedReflections = [
    {
      id: 1,
      title: "Morning Grounding",
      description: "Start your day with intention and calm",
      duration: "5:00",
      url: "https://customer-assets.emergentagent.com/job_5df331b1-9fd1-43cf-abeb-bd546fbf03f9/artifacts/8iamtc0q_morning-relaxing-144011.mp3",
      category: "Guided Reflection",
      icon: "🌅"
    },
    {
      id: 2,
      title: "Release & Let Go",
      description: "Gentle meditation for releasing what no longer serves you",
      duration: "8:00",
      url: "https://customer-assets.emergentagent.com/job_5df331b1-9fd1-43cf-abeb-bd546fbf03f9/artifacts/7654com2_Deep%20relaxation.mp3",
      category: "Guided Reflection",
      icon: "🍃"
    },
    {
      id: 3,
      title: "Healing Your Inner Child",
      description: "Compassionate practice for inner healing and self-love",
      duration: "10:00",
      url: "https://customer-assets.emergentagent.com/job_5df331b1-9fd1-43cf-abeb-bd546fbf03f9/artifacts/ixjcgxh2_Inner%20child%20.mp3",
      category: "Guided Reflection",
      icon: "💝"
    },
    {
      id: 4,
      title: "Boundary Setting Meditation",
      description: "Strengthen your ability to honor your needs",
      duration: "12:00",
      url: "https://customer-assets.emergentagent.com/job_5df331b1-9fd1-43cf-abeb-bd546fbf03f9/artifacts/jirq6u8v_boundary%20setting.mp3",
      category: "Guided Reflection",
      icon: "🛡️"
    },
    {
      id: 5,
      title: "Evening Wind Down",
      description: "Release the day and prepare for restful sleep",
      duration: "15:00",
      url: "https://customer-assets.emergentagent.com/job_5df331b1-9fd1-43cf-abeb-bd546fbf03f9/artifacts/blx4soe8_Evening%20Wind%20down%20.mp3",
      category: "Guided Reflection",
      icon: "🌙"
    }
  ];

  const soothingMusic = [
    {
      id: 6,
      title: "Peaceful Piano",
      description: "Gentle piano melodies for reflection and journaling",
      duration: "5:00",
      url: "https://customer-assets.emergentagent.com/job_5df331b1-9fd1-43cf-abeb-bd546fbf03f9/artifacts/4hm80u29_Piano%20meditation.mp3",
      category: "Soothing Music",
      icon: "🎹"
    },
    {
      id: 7,
      title: "Morning Peace Meditation",
      description: "Gentle morning meditation to center your spirit",
      duration: "8:00",
      url: "https://customer-assets.emergentagent.com/job_5df331b1-9fd1-43cf-abeb-bd546fbf03f9/artifacts/cgndj0kh_Morning%20Peace%20meditation.mp3",
      category: "Soothing Music",
      icon: "🌲"
    },
    {
      id: 8,
      title: "Healing Frequencies",
      description: "Healing music to restore your energy",
      duration: "10:00",
      url: "https://customer-assets.emergentagent.com/job_5df331b1-9fd1-43cf-abeb-bd546fbf03f9/artifacts/keogdcln_Healing%20.mp3",
      category: "Soothing Music",
      icon: "☁️"
    },
    {
      id: 9,
      title: "Mind Relaxation",
      description: "Deep mind relaxation for inner peace",
      duration: "6:00",
      url: "https://customer-assets.emergentagent.com/job_5df331b1-9fd1-43cf-abeb-bd546fbf03f9/artifacts/9rbdytmd_mind-relaxation-183925.mp3.crdownload",
      category: "Soothing Music",
      icon: "🌊"
    },
    {
      id: 10,
      title: "Healing Waves of Love",
      description: "532 Hz healing uplifting meditation music",
      duration: "12:00",
      url: "https://customer-assets.emergentagent.com/job_5df331b1-9fd1-43cf-abeb-bd546fbf03f9/artifacts/6m211e99_healing-waves-of-love-532-hz-healing-uplifting-meditation-music-332727.mp3.crdownload",
      category: "Soothing Music",
      icon: "🔔"
    },
    {
      id: 11,
      title: "Evening Stillness",
      description: "Calming evening meditation for peaceful rest",
      duration: "15:00",
      url: "https://customer-assets.emergentagent.com/job_5df331b1-9fd1-43cf-abeb-bd546fbf03f9/artifacts/whm30kea_evening%20stillness%20.mp3",
      category: "Soothing Music",
      icon: "💤"
    }
  ];

  const allAudio = [...guidedReflections, ...soothingMusic];

  const handlePlayPause = (audio) => {
    if (currentPlaying?.id === audio.id) {
      if (isPlaying) {
        audioRef.current?.pause();
        setIsPlaying(false);
      } else {
        audioRef.current?.play();
        setIsPlaying(true);
      }
    } else {
      setCurrentPlaying(audio);
      setIsPlaying(true);
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.src = audio.url;
          audioRef.current.play();
        }
      }, 100);
    }
  };

  const handleAudioEnd = () => {
    setIsPlaying(false);
  };

  return (
    <div className="min-h-screen bg-[#2E2536]">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#2E2536]/95 backdrop-blur-sm border-b border-[#C8B5E0]/20">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              size="icon"
              className="text-[#EDE8F3] hover:bg-[#C8B5E0]/20 rounded-full"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            
            <h1 className="text-2xl font-bold text-[#EDE8F3]">Moments of Stillness</h1>
            
            <div className="w-10"></div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Intro */}
        <div className="bg-[#3E324D] rounded-2xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <Volume2 className="w-8 h-8 text-[#C8B5E0] flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-xl font-semibold text-[#EDE8F3] mb-2">Pause. Breathe. Be Present.</h2>
              <p className="text-[#C8B5E0]">
                These sounds and gentle voices are here for you—to hold space when words feel too heavy, 
                or to quiet your mind when it won't stop racing. Listen whenever you need to feel held.
              </p>
            </div>
          </div>
        </div>

        {/* Guided Reflections Section */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Mic className="w-6 h-6 text-[#E7C6C2]" />
            <h2 className="text-xl font-semibold text-[#EDE8F3]">Guided Reflections</h2>
          </div>
          
          <div className="grid gap-4">
            {guidedReflections.map((audio) => (
              <div 
                key={audio.id}
                className="bg-[#3E324D] rounded-xl p-6 hover:bg-[#4A3B5A] transition-colors"
              >
                <div className="flex items-center gap-4">
                  <Button
                    onClick={() => handlePlayPause(audio)}
                    size="icon"
                    className="h-14 w-14 rounded-full bg-[#E7C6C2] hover:bg-[#F7E5E5] text-[#3E324D] flex-shrink-0"
                  >
                    {currentPlaying?.id === audio.id && isPlaying ? (
                      <Pause className="w-6 h-6" />
                    ) : (
                      <Play className="w-6 h-6 ml-1" />
                    )}
                  </Button>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl">{audio.icon}</span>
                      <h3 className="text-lg font-semibold text-[#EDE8F3] truncate">{audio.title}</h3>
                    </div>
                    <p className="text-[#C8B5E0] text-sm mb-2">{audio.description}</p>
                    <div className="flex items-center gap-4 text-xs text-[#C8B5E0]">
                      <span>{audio.duration}</span>
                      <span>•</span>
                      <span>{audio.category}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Soothing Music Section */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <Music className="w-6 h-6 text-[#AAB49F]" />
            <h2 className="text-xl font-semibold text-[#EDE8F3]">Soothing Music</h2>
          </div>
          
          <div className="grid gap-4">
            {soothingMusic.map((audio) => (
              <div 
                key={audio.id}
                className="bg-[#3E324D] rounded-xl p-6 hover:bg-[#4A3B5A] transition-colors"
              >
                <div className="flex items-center gap-4">
                  <Button
                    onClick={() => handlePlayPause(audio)}
                    size="icon"
                    className="h-14 w-14 rounded-full bg-[#AAB49F] hover:bg-[#C8D1C0] text-white flex-shrink-0"
                  >
                    {currentPlaying?.id === audio.id && isPlaying ? (
                      <Pause className="w-6 h-6" />
                    ) : (
                      <Play className="w-6 h-6 ml-1" />
                    )}
                  </Button>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl">{audio.icon}</span>
                      <h3 className="text-lg font-semibold text-[#EDE8F3] truncate">{audio.title}</h3>
                    </div>
                    <p className="text-[#C8B5E0] text-sm mb-2">{audio.description}</p>
                    <div className="flex items-center gap-4 text-xs text-[#C8B5E0]">
                      <span>{audio.duration}</span>
                      <span>•</span>
                      <span>{audio.category}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Note about audio */}
        <div className="mt-8 bg-[#3E324D]/50 rounded-xl p-4 border border-[#C8B5E0]/10">
          <p className="text-[#C8B5E0] text-sm text-center">
            💡 Tip: Use headphones for the best experience. These audios are designed to be calming and supportive.
          </p>
        </div>
      </div>

      {/* Hidden Audio Element */}
      <audio 
        ref={audioRef}
        onEnded={handleAudioEnd}
        className="hidden"
      />

      {/* Now Playing Bar (if something is playing) */}
      {currentPlaying && (
        <div className="fixed bottom-0 left-0 right-0 bg-[#3E324D] border-t border-[#C8B5E0]/20 p-4 z-20">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <span className="text-2xl">{currentPlaying.icon}</span>
              <div className="min-w-0 flex-1">
                <p className="text-[#EDE8F3] font-medium truncate">{currentPlaying.title}</p>
                <p className="text-[#C8B5E0] text-sm">{currentPlaying.category}</p>
              </div>
            </div>
            
            <Button
              onClick={() => handlePlayPause(currentPlaying)}
              size="icon"
              className="h-12 w-12 rounded-full bg-[#6C4CA2] hover:bg-[#8B6BB8] text-white flex-shrink-0"
            >
              {isPlaying ? (
                <Pause className="w-5 h-5" />
              ) : (
                <Play className="w-5 h-5 ml-1" />
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudioLibraryPage;
